import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Phase 3d — Change Decision modal.
 *
 * Renders when a HOD/Admin wants to amend their earlier reject or on-hold
 * decision. Backed by `POST /api/requisitions/{ref}/amend-decision`.
 *
 * Props:
 *   open            : boolean
 *   requisition     : requisition doc (must expose ref_code + prior audit)
 *   previousAction  : "reject" | "on_hold"
 *   previousRemarks : string (the original remarks — shown for context)
 *   onClose(), onDone(req)
 */
export default function AmendDecisionModal({
  open, requisition, previousAction, previousRemarks,
  onClose, onDone,
}) {
  const [remarks, setRemarks] = useState("");
  const [newAction, setNewAction] = useState("approve");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  // stable primitive → no unstable-object-ref bug (see Phase 3c fix)
  const refCode = requisition?.ref_code;

  useEffect(() => {
    if (!open || !refCode) return;
    setError(""); setRemarks("");
    // default new action to the "opposite" — if they rejected, propose approve;
    // if they held, propose approve. If both are same, force switch to reject.
    setNewAction(previousAction === "reject" ? "approve" : "approve");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, refCode, previousAction]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const minOk = remarks.trim().length >= 10;
  const differsFromPrev = newAction !== previousAction;

  const availableActions = useMemo(() => {
    // Must differ from previous. Keep all three but disable the one matching prev.
    return [
      { key: "approve", label: "Approve and resume workflow" },
      { key: "on_hold", label: "Place back On Hold (return to applicant)" },
      { key: "reject",  label: "Reject (close)" },
    ];
  }, []);

  const submit = async () => {
    if (!minOk) { setError("Remarks must be at least 10 characters."); return; }
    if (!differsFromPrev) {
      setError(`New action must differ from the previous ('${previousAction}').`);
      return;
    }
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${refCode}/amend-decision`,
        { new_action: newAction, remarks: remarks.trim() },
      );
      toast.success(`Decision amended for ${refCode}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="amend-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[#e2d7c3] px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-serif text-lg font-semibold text-[#1f2320]">
              Change decision · {refCode}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-medium">
              You previously chose: <span className="text-[#b3261e]">{previousAction === "reject" ? "Rejected" : "On Hold"}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="amend-close"
                  className="text-[#6e7770] hover:text-[#1f2320] text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-4">
          {previousRemarks && (
            <div className="text-xs text-[#4a524c] bg-[#faf5eb] border border-[#e2d7c3] rounded px-3 py-2">
              <div className="uppercase tracking-widest text-[10px] text-[#8a6a2f] font-semibold mb-1">
                Your previous remarks
              </div>
              <div className="whitespace-pre-wrap text-[#1f2320]">{previousRemarks}</div>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#4a524c] font-semibold mb-2">
              New decision
            </label>
            <div className="flex flex-col gap-1.5" data-testid="amend-action-choices">
              {availableActions.map((a) => {
                const isPrev = a.key === previousAction;
                return (
                  <label key={a.key}
                         className={`flex items-center gap-2 text-sm cursor-pointer px-2 py-1 rounded ${
                    newAction === a.key ? "bg-[#f4e9eb]" : "hover:bg-[#faf5eb]"
                  } ${isPrev ? "opacity-40 cursor-not-allowed" : ""}`}>
                    <input
                      type="radio"
                      name="amend-action"
                      value={a.key}
                      disabled={isPrev}
                      checked={newAction === a.key}
                      onChange={() => setNewAction(a.key)}
                      data-testid={`amend-action-${a.key}`}
                    />
                    <span className="text-[#1f2320]">{a.label}</span>
                    {isPrev && <span className="text-[10px] uppercase text-[#6e7770]">(previous)</span>}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-[#4a524c] font-semibold mb-2">
              Amendment remarks (min 10 chars)
            </label>
            <textarea
              ref={textareaRef}
              rows={4}
              className="input-vgws w-full"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Explain why you're changing your earlier decision (spoke to applicant, new information, etc.)"
              data-testid="amend-remarks"
            />
            <div className="text-[10px] text-[#6e7770] text-right mt-1">
              {remarks.trim().length} / min 10
            </div>
          </div>

          {error && (
            <div className="text-xs text-[#b3261e] bg-[#fcebe9] border border-[#b3261e]/30 rounded px-3 py-2"
                 data-testid="amend-error">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-[#e2d7c3] px-5 py-3 flex items-center justify-end gap-2">
          <button
            className="px-3 py-1.5 text-sm rounded border border-[#e2d7c3] hover:bg-[#faf5eb] text-[#4a524c]"
            onClick={onClose}
            disabled={busy}
            data-testid="amend-cancel"
          >
            Cancel
          </button>
          <button
            className="px-4 py-1.5 text-sm rounded bg-[#7a1f2b] text-white hover:bg-[#5a1520] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={submit}
            disabled={!minOk || !differsFromPrev || busy}
            data-testid="amend-confirm"
          >
            {busy ? "Submitting…" : "Confirm amendment"}
          </button>
        </div>
      </div>
    </div>
  );
}
